import { FC, useRef } from "react";
import {
  CoreFieldValue,
  FieldFormV2,
  FieldFormV2Module,
  FieldV2,
  SecondaryCard,
  SecondaryButton,
  SecondaryCardHeader,
  LoadingV2,
} from "@ucl/library";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { PersonalAccountBeneficiary, PersonInformation } from "@/types";
import { FIELD_KEYS } from "@/constants/fieldKeys";
import { usePersonalAccountBeneficiary } from "@/hooks/usePersonalAccountBeneficiary";
import { getFullName, PersonInformationComplexValue } from "@/components/ComplexFields/PersonInformationComplexField";

import { PersonalAccountBeneficiaryBequests } from "./PersonalAccountBeneficiaryBequests";
import { FinancialApplicationFormSectionContextType } from "@/hooks/useFinancialApplicationFormSectionValidation";

export interface PersonalAccountBeneficiaryFormProps {
  financialApplicationId: string;
  personalAccountBeneficiaryId: string;
  validationErrors?: Record<string, Record<string, string[]>>;
  onBlur?: (field: CoreFieldValue) => void;
  readOnly?: boolean;
  onDelete?: (personalAccountBeneficiaryId: string) => void;
  isInitiallyCollapsed?: boolean;
  allowChangingWhetherCollapsed?: boolean;
  validationContextType: FinancialApplicationFormSectionContextType;
}

export const PersonalAccountBeneficiaryForm: FC<PersonalAccountBeneficiaryFormProps> = (props) => {
  const fieldFormModule = useRef<FieldFormV2Module<PersonalAccountBeneficiary> | null>(null);

  const { formValue, setFormValue, saveFormData, isLoading, formBuilder } =
    usePersonalAccountBeneficiary(
      props.financialApplicationId,
      props.personalAccountBeneficiaryId,
      props.validationContextType
    );

  if (!formBuilder || !formValue || isLoading) {
    return (
      <section className="personal-account-beneficiary-form-section">
        <LoadingV2 />
      </section>
    );
  }

  return (
    <section className="personal-account-beneficiary-form-section">
      <SecondaryCard
        hideBody={props.isInitiallyCollapsed ?? false}
        isCollapsible={props.allowChangingWhetherCollapsed ?? true}
        headerContent={
          <>
            <div className="personal-account-beneficiary-form__header">
              <SecondaryCardHeader
                label={
                  getFullName(formValue?.personInformation as PersonInformation) ||
                  "New Beneficiary"
                }
              />
              {props.validationErrors?.[props.personalAccountBeneficiaryId] &&
                Object.keys(props.validationErrors[props.personalAccountBeneficiaryId]).length >
                  0 && (
                  <span className="personal-account-beneficiary-form__validation-errors e-badge e-badge-pill">
                    {
                      Object.keys(props.validationErrors?.[props.personalAccountBeneficiaryId])
                        .length
                    }
                  </span>
                )}
            </div>
            {!props.readOnly && props.onDelete && (
              <SecondaryButton
                minimal={true}
                iconProps={{ icon: faTrash }}
                cssClass="subtle danger-button"
                onClick={() => props.onDelete!(props.personalAccountBeneficiaryId)}
                disabled={props.readOnly}
              />
            )}
          </>
        }
      >
        <div className="personal-account-beneficiary-form">
          <FieldFormV2<PersonalAccountBeneficiary>
            ref={fieldFormModule}
            value={formValue}
            fieldSchemaFormBuilder={formBuilder}
            onFormSubmit={async (value) => {
              await saveFormData(value);
            }}
            onFieldChanged={(newValue, newFieldValue, fieldProps) => {
              setFormValue(newValue, newFieldValue, fieldProps);
            }}
            errors={props.validationErrors?.[props.personalAccountBeneficiaryId] || {}}
          >
            <div className="personal-account-beneficiary-relationship-information">
              <FieldV2
                fieldProps={FIELD_KEYS.PERSONAL_ACCOUNT_BENEFICIARY.RELATIONSHIP_TO_PRIMARY_OWNER}
                handleOnBlur={props.onBlur}
              />
            </div>
            <FieldV2<PersonInformationComplexValue>
              fieldProps={FIELD_KEYS.PERSONAL_ACCOUNT_BENEFICIARY.PERSON_INFORMATION}
              complexFieldValue={{
                hiddenFields: ["PHONE_NUMBER", "EMAIL_ADDRESS", "HOME_ADDRESS"],
                showHeader: true,
                headerLabel: "Personal Information",
                className: "personal-information",
              }}
              handleOnBlur={props.onBlur}
            />
          </FieldFormV2>

          <PersonalAccountBeneficiaryBequests
            financialApplicationId={props.financialApplicationId}
            personalAccountBeneficiaryId={props.personalAccountBeneficiaryId}
            readOnly={props.readOnly}
          />
        </div>
      </SecondaryCard>
    </section>
  );
};
